/**
 * Pure H OS · Workstream 04 · Inventory · Slice 1 shell
 * -----------------------------------------------------------------
 * Warehouse & Ledger foundation. Read-only tab shell that hosts:
 *   /pureh/inventory/warehouses  · list + detail
 *   /pureh/inventory/grn         · list + editor
 *   /pureh/inventory/adjustments · new adjustment
 *   /pureh/inventory/report      · on-hand snapshot
 */
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Boxes, ClipboardList, Sliders, PieChart } from "lucide-react";

const TABS: { to: string; label: string; testId: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { to: "/pureh/inventory/warehouses",   label: "Warehouses",  testId: "nav-inv-warehouses",  icon: Boxes },
  { to: "/pureh/inventory/grn",          label: "GRN",         testId: "nav-inv-grn",         icon: ClipboardList },
  { to: "/pureh/inventory/adjustments",  label: "Adjustments", testId: "nav-inv-adjustments", icon: Sliders },
  { to: "/pureh/inventory/report",       label: "On-hand",     testId: "nav-inv-report",      icon: PieChart },
];

export default function InventoryLayout() {
  const loc = useLocation();
  return (
    <div className="px-6 py-8 max-w-[1400px] mx-auto" data-testid="inventory-layout">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Workstream 04</div>
        <h1 className="text-3xl md:text-4xl font-serif mt-1" style={{ color: "var(--ph-ink-strong)" }}>
          Inventory <span className="text-slate-400 font-normal text-lg">· Slice 1</span>
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          The physical-truth ledger for raw material and packaging lots. Warehouses,
          bins, goods-received notes, stock adjustments and an on-hand report — every
          value backed by a signed movement.
        </p>
      </header>
      <nav
        className="inline-flex items-center gap-1 p-1 mb-6"
        style={{
          background: "var(--ph-surface)",
          border: "1px solid var(--ph-border-subtle)",
          borderRadius: "var(--ph-radius-md)",
        }}
        data-testid="inventory-tabs"
      >
        {TABS.map((t) => {
          const active = loc.pathname.startsWith(t.to);
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testId}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                background: active ? "var(--ph-surface-card)" : "transparent",
                color: active ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
                borderRadius: "var(--ph-radius-sm)",
                boxShadow: active ? "var(--ph-shadow-card)" : "none",
                border: active ? "1px solid var(--ph-border-subtle)" : "1px solid transparent",
              }}
            >
              <Icon size={13} /> {t.label}
            </NavLink>
          );
        })}
      </nav>
      <Outlet />
    </div>
  );
}
